import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { Tour } from '../../api/types';
import { resolveImg } from '../../utils/img';

type Draft = Partial<Tour> & { includedText?: string; notIncludedText?: string; includedEnText?: string; notIncludedEnText?: string };

const empty: Draft = {
  title: '', subtitle: '', durationDays: 3, bookingMode: 'SCHEDULED', description: '',
  pricePerPersonPerDay: 75, depositPercent: 25, minPeople: 3,
  maxPeoplePerBooking: 8, minDays: 0, maxDays: 0, imageUrl: '',
  includedText: '', notIncludedText: '', active: true, sortOrder: 0,
};

const ToursAdmin: React.FC = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.tours.list().then(setTours).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const edit = (t: Tour) => setDraft({
    ...t,
    includedText: (t.included || []).join('\n'),
    notIncludedText: (t.notIncluded || []).join('\n'),
    includedEnText: (t.includedEn || []).join('\n'),
    notIncludedEnText: (t.notIncludedEn || []).join('\n'),
  });

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const body: Partial<Tour> = {
      ...draft,
      included: (draft.includedText || '').split('\n').map((s) => s.trim()).filter(Boolean),
      notIncluded: (draft.notIncludedText || '').split('\n').map((s) => s.trim()).filter(Boolean),
      includedEn: (draft.includedEnText || '').split('\n').map((s) => s.trim()).filter(Boolean),
      notIncludedEn: (draft.notIncludedEnText || '').split('\n').map((s) => s.trim()).filter(Boolean),
    };
    delete (body as any).includedText;
    delete (body as any).notIncludedText;
    delete (body as any).includedEnText;
    delete (body as any).notIncludedEnText;
    try {
      if (draft.id) await adminApi.tours.update(draft.id, body);
      else await adminApi.tours.create(body);
      setDraft(null);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare questo tour?')) return;
    await adminApi.tours.remove(id);
    load();
  };

  const field = (label: string, el: React.ReactNode) => (
    <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">{label}</label>{el}</div>
  );
  const input = 'w-full border border-neutral-200 rounded px-3 py-2 text-sm';

  if (loading) return <p className="text-neutral-500">Caricamento…</p>;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Tour & Prezzi</h1>
          <p className="text-neutral-500 text-sm">Aggiungi tour, modifica descrizioni e prezzi.</p>
        </div>
        <button onClick={() => setDraft({ ...empty })} className="px-4 py-2 bg-[#E4FF1A] text-[#0B0B0C] font-medium rounded text-sm">+ Nuovo tour</button>
      </div>

      <div className="space-y-3">
        {tours.map((t) => (
          <div key={t.id} className="bg-white border border-neutral-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3">
            <div>
              <p className="font-medium">{t.title} {!t.active && <span className="text-xs text-red-500">(non attivo)</span>}</p>
              <p className="text-sm text-neutral-500">{t.subtitle} · €{t.pricePerPersonPerDay}/persona/giorno · acconto {t.depositPercent}% · {t.bookingMode === 'FLEXIBLE' ? 'calendario' : 'partenze fisse'}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => edit(t)} className="text-sm text-blue-600 hover:underline">Modifica</button>
              <button onClick={() => remove(t.id)} className="text-sm text-red-500 hover:underline">Elimina</button>
            </div>
          </div>
        ))}
      </div>

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setDraft(null)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4">{draft.id ? 'Modifica tour' : 'Nuovo tour'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('Titolo', <input className={input} value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />)}
              {field('Sottotitolo', <input className={input} value={draft.subtitle || ''} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} />)}
              {field('Durata (giorni)', <input type="number" className={input} value={draft.durationDays ?? 0} onChange={(e) => setDraft({ ...draft, durationDays: Number(e.target.value) })} />)}
              {field('Modalità prenotazione', (
                <select className={input} value={draft.bookingMode || 'SCHEDULED'} onChange={(e) => setDraft({ ...draft, bookingMode: e.target.value as Tour['bookingMode'] })}>
                  <option value="SCHEDULED">Partenze fisse (slot)</option>
                  <option value="FLEXIBLE">Calendario a periodo</option>
                </select>
              ))}
              {field('Min. persone', <input type="number" className={input} value={draft.minPeople ?? 1} onChange={(e) => setDraft({ ...draft, minPeople: Number(e.target.value) })} />)}
              {field('Prezzo persona/giorno (€)', <input type="number" className={input} value={draft.pricePerPersonPerDay ?? 0} onChange={(e) => setDraft({ ...draft, pricePerPersonPerDay: Number(e.target.value) })} />)}
              {field('Acconto (%)', <input type="number" className={input} value={draft.depositPercent ?? 25} onChange={(e) => setDraft({ ...draft, depositPercent: Number(e.target.value) })} />)}
              {field('Max persone / prenotazione (0 = illimitato)', <input type="number" className={input} value={draft.maxPeoplePerBooking ?? 0} onChange={(e) => setDraft({ ...draft, maxPeoplePerBooking: Number(e.target.value) })} />)}
              {field('Giorni min (0 = usa durata)', <input type="number" className={input} value={draft.minDays ?? 0} onChange={(e) => setDraft({ ...draft, minDays: Number(e.target.value) })} />)}
              {field('Giorni max (0 = illimitato)', <input type="number" className={input} value={draft.maxDays ?? 0} onChange={(e) => setDraft({ ...draft, maxDays: Number(e.target.value) })} />)}
              <div className="col-span-2">{field('Descrizione', <textarea className={input + ' min-h-[80px]'} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />)}</div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Immagine del tour</label>
                <div className="flex items-center gap-3">
                  {draft.imageUrl
                    ? <img src={resolveImg(draft.imageUrl)} alt="" className="h-16 w-24 object-cover bg-neutral-50 border rounded" />
                    : <div className="h-16 w-24 bg-neutral-100 border rounded flex items-center justify-center text-[10px] text-neutral-400">nessuna</div>}
                  <label className="text-sm px-3 py-2 bg-[#0B0B0C] text-white rounded cursor-pointer hover:opacity-90">
                    {uploadingImg ? 'Caricamento…' : 'Carica immagine'}
                    <input type="file" accept="image/*" hidden disabled={uploadingImg}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setUploadingImg(true);
                        try {
                          const { url } = await adminApi.media.upload(f, 'tours');
                          setDraft((d) => (d ? { ...d, imageUrl: url } : d));
                        } finally { setUploadingImg(false); }
                      }} />
                  </label>
                </div>
                <input className={input + ' mt-2'} value={draft.imageUrl || ''} onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })} placeholder="…oppure incolla un URL" />
              </div>
              {field('Comprende (uno per riga)', <textarea className={input + ' min-h-[100px]'} value={draft.includedText || ''} onChange={(e) => setDraft({ ...draft, includedText: e.target.value })} />)}
              {field('Non comprende (uno per riga)', <textarea className={input + ' min-h-[100px]'} value={draft.notIncludedText || ''} onChange={(e) => setDraft({ ...draft, notIncludedText: e.target.value })} />)}

              <div className="col-span-2 mt-2 pt-3 border-t">
                <p className="text-xs uppercase tracking-widest text-neutral-400">English (opzionale — se vuoto usa l'italiano)</p>
              </div>
              {field('Title (EN)', <input className={input} value={draft.titleEn || ''} onChange={(e) => setDraft({ ...draft, titleEn: e.target.value })} />)}
              {field('Subtitle (EN)', <input className={input} value={draft.subtitleEn || ''} onChange={(e) => setDraft({ ...draft, subtitleEn: e.target.value })} />)}
              <div className="col-span-2">{field('Description (EN)', <textarea className={input + ' min-h-[80px]'} value={draft.descriptionEn || ''} onChange={(e) => setDraft({ ...draft, descriptionEn: e.target.value })} />)}</div>
              {field('Included (EN, one per line)', <textarea className={input + ' min-h-[100px]'} value={draft.includedEnText || ''} onChange={(e) => setDraft({ ...draft, includedEnText: e.target.value })} />)}
              {field('Not included (EN, one per line)', <textarea className={input + ' min-h-[100px]'} value={draft.notIncludedEnText || ''} onChange={(e) => setDraft({ ...draft, notIncludedEnText: e.target.value })} />)}

              {field('Ordine', <input type="number" className={input} value={draft.sortOrder ?? 0} onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })} />)}
              <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Attivo</label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDraft(null)} className="px-4 py-2 text-sm text-neutral-500">Annulla</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-[#0B0B0C] text-white rounded text-sm disabled:opacity-60">{saving ? 'Salvataggio…' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToursAdmin;
